import React from 'react';
import { Check, X } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/OneButtonFormControls.css';

export interface OBDAddQuoteFormState {
    sourceKey: string;
    partner: string;
    product: string;
    pricingMethod: string;
    solarRate: string;
    solarEscalator: string;
    grossPpw: string;
    downPayment: string;
    term: string;
    apr: string;
}

interface OBDAddQuoteModalProps {
    formState: OBDAddQuoteFormState;
    liveMonthly: number;
    onUpdate: (field: keyof OBDAddQuoteFormState, value: string) => void;
    onSubmit: () => void;
    onClose: () => void;
    hasExistingAppSameMethod?: boolean;
}

const OBDAddQuoteModal: React.FC<OBDAddQuoteModalProps> = ({
    formState, liveMonthly, onUpdate, onSubmit, onClose, hasExistingAppSameMethod,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    return (
        <DashboardModal
            open={true}
            onClose={onClose}
            title="Add Quote"
            width={520}
            theme={theme}
            rootClassName={`obd-form-controls-modal-root obd-add-quote-modal-root obd-theme-${theme}`}
            className="obd-form-controls-modal obd-add-quote-modal"
            maskClosable
            footer={(
                <>
                    <button className="obd-modal__cancel" onClick={onClose} type="button">
                        <X size={14} weight="bold" />
                        Cancel
                    </button>
                    <button className="obd-modal__save" onClick={onSubmit} type="button">
                        <Check size={14} weight="bold" />
                        Add Quote
                    </button>
                </>
            )}
        >
            <div className="obd-modal__body obd-add-quote-modal__body">
                <div className="obd-add-quote-modal__top">
                    <OBDSelect
                        className="obd-utility__select"
                        value={formState.pricingMethod}
                        onChange={(value) => onUpdate('pricingMethod', value)}
                        style={{ width: '100%' }}
                        options={[
                            { value: 'PPW - Solar Rate', label: 'PPW - Solar Rate' },
                            { value: 'Cash', label: 'Cash' },
                            { value: 'Lease', label: 'Lease' },
                            { value: 'PPA', label: 'PPA' },
                        ]}
                    />
                    <div className="obd-add-quote-modal__monthly">
                        <span style={{ fontSize: 10, opacity: 0.7 }}>Est. monthly</span>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>
                            ${liveMonthly.toLocaleString()}{formState.pricingMethod !== 'Cash' && <span style={{ fontSize: 11, opacity: 0.7 }}>/mo</span>}
                        </span>
                    </div>
                </div>
                {hasExistingAppSameMethod && (
                    <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.6)', fontSize: 12, color: 'rgba(252,211,77,0.95)', marginBottom: 12 }}>
                        There is already an active contract for this method. After adding, you'll see Change Order.
                    </div>
                )}
                <div className="obd-utility__fields obd-add-quote-modal__fields">
                    <div className="obd-utility__field">
                        <label className="obd-utility__field-label">APR (%)</label>
                        <input type="number" step="0.01" className="obd-utility__input" value={formState.apr} onChange={(e) => onUpdate('apr', e.target.value)} placeholder="4.99" />
                    </div>
                    <div className="obd-utility__field">
                        <label className="obd-utility__field-label">Term (yr)</label>
                        <input type="number" className="obd-utility__input" value={formState.term} onChange={(e) => onUpdate('term', e.target.value)} placeholder="25" />
                    </div>
                    <div className="obd-utility__field">
                        <label className="obd-utility__field-label">PPW ($)</label>
                        <input type="number" step="0.01" className="obd-utility__input" value={formState.grossPpw} onChange={(e) => onUpdate('grossPpw', e.target.value)} placeholder="2.50" />
                    </div>
                    <div className="obd-utility__field">
                        <label className="obd-utility__field-label">Down ($)</label>
                        <input type="number" step="1" className="obd-utility__input" value={formState.downPayment} onChange={(e) => onUpdate('downPayment', e.target.value)} placeholder="0" />
                    </div>
                </div>
            </div>
        </DashboardModal>
    );
};

export default OBDAddQuoteModal;
